import React, {useContext, useState} from "react";
import {
    Alert, AlertColor,
    Button,
    Container,
    Snackbar,
    TextField,
    Typography,
    Box,
    Checkbox, FormControlLabel
} from "@mui/material";
import {StylesContext} from "../../providers/StylesProvider";
import { AuthContext } from "../../providers/AuthProvider";
import DocumentUploadWidget from "../guiComponents/DocumentUploadWidget";
import treesourceLogo from "../Loaders/graphic-test3-01.svg";
import {CustomerContext} from "../../providers/CustomerProvider";

const INITIAL_CAMERA_STATE = "Take new picture";

export type UploadListProps = {
    title?: string;
}

const UploadList = (props: UploadListProps) => {
    // Add Auth context
    const { parentCustomerName } = useContext(CustomerContext);
    const Auth = useContext(AuthContext);
    const salesRepName = Auth.user?.customer?.salesRep?.name?.split(" ")[0];

    // Data for submit
    const [deliveryDate, setDeliveryDate] = useState("");
    const [deliveryAddress, setDeliveryAddress] = useState("");
    const [jobName, setJobName] = useState("");
    const [fileList, setFileList] = useState<FileList | null>(null);
    const [myTextList, setMyTextList] = useState("");
    const [cameraWidgetState, setCameraWidgetState] = React.useState(INITIAL_CAMERA_STATE);

    // Photo Blob
    const [photoBlob, setPhotoBlob] = useState<Blob | null>();

    // UI Controls
    const { isDesktop } = useContext(StylesContext);
    const [toastOpen, setToastOpen] = React.useState(false);
    const [prefersTyping, setPrefersTyping] = useState(false);
    const [toastSeverity, setToastSeverity] = useState<AlertColor | undefined>();
    const [toastMessage, setToastMessage] = React.useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const sendPostRequest = async (files: FileList | null, date: string, address: string) => {
        const formData = new FormData();
        const fileArray = Array.from(files || []);

        const today = new Date().toISOString().split('T')[0];
        const fileName = `${today}_${Auth.user?.firstName ?? 'unknown'}_list`;

        if (prefersTyping && myTextList) {
            const textBlob = new Blob([myTextList], { type: 'text/plain' });
            const textFile = new File([textBlob], fileName + '.txt', { type: 'text/plain' });
            formData.append('file', textFile);
        } else if (files) {
            console.log("Files: ", fileArray)

            fileArray.forEach(file => formData.append('file', file));
        } else if(photoBlob) {
            formData.append('file', new File([photoBlob], fileName + '.jpg'));
        } else {
            setToastSeverity("error")
            setToastMessage("No files selected.")
            setToastOpen(true)

            return;
        }

        formData.append('deliveryDate', date);
        formData.append('deliveryAddress', address);
        formData.append('jobName', jobName);
        formData.append("customerName", parentCustomerName ?? "");

        const response = await fetch('/api/lists', {
            method: 'POST',
            body: formData,
            headers: {
                Authorization: localStorage.getItem("token") ?? undefined,
            } as HeadersInit
        });

        if (!response.ok) {
            throw new Error('Failed to send POST request');
        }
    };

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        setIsLoading(true);
        setIsSuccess(false);

        try {
            await sendPostRequest(fileList, deliveryDate, deliveryAddress);

            setDeliveryDate("");
            setDeliveryAddress("");
            setFileList(null);
            setMyTextList(""); // Clear the text input after successful submission
            setJobName("");
            setPrefersTyping(false);
            setIsSuccess(true);

            // Scroll to the top of the page
            window.scrollTo(0, 0);
        } catch (error) {
            console.error(error);

            setToastMessage("Failed to submit list. Please try again later.");
            setToastSeverity("error");
            setToastOpen(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setToastOpen(false);
    };

    const toggleTyping = () => {
        setMyTextList("");
        setFileList(null)
        setPhotoBlob(null)
        setCameraWidgetState(INITIAL_CAMERA_STATE);
        setPrefersTyping(!prefersTyping);
    }

    const renderToast = () => {
        return (
            <Snackbar autoHideDuration={4000} open={toastOpen} onClose={handleClose}>
                <Alert
                    onClose={handleClose}
                    severity={toastSeverity}
                    style={{ width: "50%" }}
                >
                    {toastMessage}
                </Alert>
            </Snackbar>
        );
    };

    const formValid = () => {
        return (!prefersTyping && ((fileList && fileList.length > 0) || photoBlob))
                || (prefersTyping && myTextList && myTextList.length > 0);
    }

    const renderDocumentControl = () => {
        if(prefersTyping) {
            return <div style={{display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", marginBottom: 10, marginTop: 10}}>
                <TextField multiline fullWidth rows={15} value={myTextList} onChange={e => {setMyTextList(e.target.value)}}/>
            </div>
        } else {
            return <DocumentUploadWidget setPhotoBlob={setPhotoBlob} setFileList={setFileList} fileList={fileList} widgetState={cameraWidgetState} setWidgetState={setCameraWidgetState}/>
        }
    }

    return (
        <>
            <Container style={{marginTop: isDesktop ? 85 : 60, marginBottom: 50}}>
                {isLoading && (
                    <div style={{display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100%"}}>
                        <img style={{width: 200}} src={treesourceLogo} alt="treesource loading spinner"/>
                        <p>Sending your list to {salesRepName}...</p>
                    </div>
                )}

                {isSuccess && (
                    <Box sx={{ 
                        display: "flex", 
                        flexDirection: "column", 
                        alignItems: "center", 
                        justifyContent: "center",
                        textAlign: "center",
                        py: 4 
                    }}>
                        <Typography variant="h4"  gutterBottom>
                            List Submitted Successfully!
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ mt: 3 }}
                            onClick={() => {
                                setIsSuccess(false)
                                setPhotoBlob(null)
                            }}
                        >
                            Submit Another List
                        </Button>
                    </Box>
                )}
                
                {!isLoading && !isSuccess && (
                    <>
                        <Typography variant="h6">
                            {props.title || (salesRepName ? `Send a list to ${salesRepName}!` : "Upload Your List")}
                        </Typography>
                        <hr/>

                        <div style={{marginTop: 20, marginBottom: 10}}>
                            <TextField
                                id="deliveryDate"
                                fullWidth
                                label="When is your job happening? (Estimate)"
                                type="date"
                                value={deliveryDate}
                                onChange={(e) => setDeliveryDate(e.target.value)}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </div>

                        <div style={{marginTop: 10, marginBottom: 10}}>
                            <TextField
                                id="jobName"
                                fullWidth
                                label="What is a good job name? (Optional)"
                                type="text"
                                value={jobName}
                                onChange={(e) => setJobName(e.target.value)}
                                inputProps={{ maxLength: 25 }}
                            />
                        </div>

                        <div style={{marginTop: 10, marginBottom: 10}}>
                            <TextField
                                id="deliveryAddress"
                                fullWidth
                                label="Where is your job? (Optional)"
                                type="text"
                                multiline
                                rows={2}
                                value={deliveryAddress}
                                onChange={(e) => setDeliveryAddress(e.target.value)}
                                inputProps={{ maxLength: 200 }}
                            />
                        </div>

                        <FormControlLabel style={{marginLeft: 2}} control={<Checkbox value={prefersTyping} onChange={() => toggleTyping()} />} label="I prefer to type my list." />
                        {renderDocumentControl()}

                        <div
                            id="buttonGroup"
                            style={{display: "flex"}}
                        >
                            <Button
                                style={{
                                    flex: "1",
                                    marginBottom: 20
                                }}
                                id="submitListButton"
                                type="submit"
                                variant="contained"
                                color="success"
                                onClick={handleSubmit}
                                disabled={!formValid()}
                            >
                                Submit List
                            </Button>
                        </div>
                    </>
                )}
            </Container>
            {renderToast()}
        </>
    );
}

export default UploadList; 